import { faFileLines } from '@fortawesome/pro-regular-svg-icons'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useStyles } from './MarketplaceRecipientSourceTab.styles'
import { Typography } from '@mui/material'
import {
  CheckBoxFieldv2,
  RadioButtonGroupMapping,
  RadioButtonsV2,
  RadioButtonType,
} from '@labrav/react-components'
import { useMemo } from 'react'
import { useTheme } from '@mui/material/styles'
import { useSelector } from '../../../../../../store'
import { shallowEqual, useDispatch } from 'react-redux'
import {
  MarketplaceFieldMappingRecipientSource,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../../../../modules/marketplaceMappingManager/types'
import { updateMarketplaceTemplateSectionField } from '../../../../../../modules/marketplaceMappingManager/action'

type MarketplaceRecipientSourceTabProps = {
  crmLabel?: 'Salesforce' | 'HubSpot'
  isAzureHubspot?: boolean
}

export const MarketplaceRecipientSourceTab = ({
  crmLabel = 'Salesforce',
  isAzureHubspot = false,
}: MarketplaceRecipientSourceTabProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const marketplaceTableDetails = useSelector(
    state =>
      state.marketplaceMappingManager?.templateData?.templateData?.fieldMappings
        ?.recipientSource?.recipientSourceMapping,
    shallowEqual
  )
  const radioKey = useMemo(
    () => Object.keys(marketplaceTableDetails || {})[0],
    [marketplaceTableDetails]
  )
  const recipientSourceKey = radioKey || 'Labra__Recipient_Source__c'
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateMarketplaceTemplateSectionField(
        'recipientSource' as keyof MarketplaceTemplatesDataFieldMappings,
        {
          recipientSourceMapping: {
            ...marketplaceTableDetails,
            [recipientSourceKey]: {
              ...marketplaceTableDetails?.[recipientSourceKey],
              referralField: recipientSourceKey,
              referralFieldType: 'Picklist',
              label: 'Recipient source',
              isHidden: false,
              isReadonly: false,
              required: false,
              displayOrder: 0,
              category: 'Recipient source',
              picklist: null,
              picklistMappings: null,
              maxLength: null,
              minLength: null,
              description: '',
              placeholder: '',
              defaultValue: event.target.value,
              isMapped: !!event.target.value,
            } as MarketplaceFieldMappingRecipientSource,
          },
        } as Partial<MarketplaceFieldMappingRecipientSource>
      )
    )
  }
  const handleAutoFillChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(
      updateMarketplaceTemplateSectionField(
        'recipientSource' as keyof MarketplaceTemplatesDataFieldMappings,
        {
          recipientSourceMapping: {
            ...marketplaceTableDetails,
            [recipientSourceKey]: {
              ...marketplaceTableDetails?.[recipientSourceKey],
              referralField: recipientSourceKey,
              referralFieldType: 'Picklist',
              label: 'Recipient source',
              isHidden: false,
              isReadonly: false,
              required: false,
              displayOrder: 0,
              category: 'Recipient source',
              picklist: null,
              picklistMappings: null,
              maxLength: null,
              minLength: null,
              description: '',
              placeholder: '',
              defaultValue: event.target.checked ? 'Contacts' : '',
              isMapped: event.target.checked,
              autoFillRecipients: event.target.checked,
            } as MarketplaceFieldMappingRecipientSource,
          },
        } as Partial<MarketplaceFieldMappingRecipientSource>
      )
    )
  }
  const autoFillRecipients =
    marketplaceTableDetails?.[recipientSourceKey]?.autoFillRecipients ??
    marketplaceTableDetails?.[recipientSourceKey]?.defaultValue === 'Contacts'
  const radioButtonGroups: RadioButtonGroupMapping[] = [
    {
      key: 'Contacts',
      label: 'Contacts',
      type: 'STANDARD' as RadioButtonType,
      descriptionPlacement: 'right',
    },
    {
      key: 'Contact Roles',
      label: 'Contact Roles',
      type: 'STANDARD' as RadioButtonType,
      descriptionPlacement: 'right',
    },
  ]
  const radioButtonsInfo = {
    checkedValue: {
      key: recipientSourceKey,
      value: (marketplaceTableDetails?.[recipientSourceKey]?.defaultValue ||
        '') as string,
    },
    handleChange,
    radioButtonMappings: radioButtonGroups,
    gapBetween: useTheme().spacing(3),
    alignSameHeight: true,
  }
  return (
    <>
      <div
        data-testid="marketplace-recipient-source-tab"
        className={classes.tabWrapper}
      >
        <div className={classes.headingContainer}>
          <div className={classes.iconWrapper}>
            <FontAwesomeIcon icon={faFileLines} className={classes.icon} />
          </div>
          <Typography className={classes.heading}>
            Choose source of recipient
          </Typography>
        </div>
        <div className={classes.radioSectionWrapper}>
          <Typography className={classes.radioHeading}>
            Select the {crmLabel} object to fetch recipients from.
          </Typography>
          {isAzureHubspot ? (
            <div className={classes.autoFillOption}>
              <CheckBoxFieldv2
                keyName="marketplace-recipient-source"
                title="Contacts"
                value={autoFillRecipients}
                onChangeBox={handleAutoFillChange}
              />
            </div>
          ) : (
            <RadioButtonsV2 {...radioButtonsInfo} />
          )}
        </div>
      </div>
    </>
  )
}
